import { memo, useMemo } from "react"

import type { BrandfetchData } from "@productlane/api/src/browser"
import type { AvatarProps } from "@productlane/ui"
import { cn } from "@productlane/lib/src/browser"
import { Avatar, useDarkMode } from "@productlane/ui"

interface CompanyLogoProps {
	brandfetchData: BrandfetchData | undefined | null
	fallback?: string
	className?: string
	contactImageUrl?: string | null
	size?: AvatarProps["size"]
	roundness?: AvatarProps["roundness"]
	linearLogoUrl?: string | undefined
	onClick?: () => void
}

// Sorts logos to prefer "icon" logos over "logo" logos
// type:"logo" logos have the text, while the ones with type:"icon" only have the symbol/actual logo
export const pickCompanyLogoFormat = (
	brandfetchData: BrandfetchData | undefined | null,
	isDarkMode: boolean,
) => {
	// this is inverse to our preferred theme, if it's dark mode we want the light icon so that we dont get white on white
	const logoTheme = isDarkMode ? "light" : "dark"
	const logo =
		// icons are best and with correct theme
		brandfetchData?.logos.find(
			(l) => l.type === "icon" && l.theme === logoTheme,
		) ??
		brandfetchData?.logos.find((l) => l.type === "icon") ??
		brandfetchData?.logos[1] ??
		brandfetchData?.logos[0]

	const formats = logo ? [...logo.formats] : []
	return formats.sort((a) => (a.background === "transparent" ? 1 : -1))[0]
}

export const useCompanyLogoStyles = (
	brandfetchData: BrandfetchData | undefined | null,
) => {
	const { isDarkMode } = useDarkMode()

	const format = useMemo(
		() => pickCompanyLogoFormat(brandfetchData, isDarkMode),
		[brandfetchData, isDarkMode],
	)

	const accentColor = useMemo(
		() => brandfetchData?.colors.find((c) => c.type === "accent"),
		[brandfetchData?.colors],
	)

	const bgColor = useMemo(() => {
		const colors = brandfetchData ? [...brandfetchData.colors] : []
		return colors.sort((_a, b) => (b.type === "dark" && isDarkMode ? -1 : 1))[0]
	}, [brandfetchData, isDarkMode])

	// This is only here to sort out ugly gray-ish colors on darkmode.
	// A good example of this, when this component was first built, is the Google logo. It had a flat gray bg that clashed with ours a lot
	const bgColorHex = useMemo(() => {
		if (isDarkMode) {
			return "var(--gray-800)"
		}
		return "var(--gray-100)"
	}, [isDarkMode])

	const style = {
		backgroundColor:
			format?.background === "transparent" || !format ? bgColorHex : undefined,
		color: accentColor?.hex ?? "black",
	}

	return {
		format,
		accentColor,
		bgColor,
		bgColorHex,
		style,
	}
}

export const CompanyLogo = memo((props: CompanyLogoProps) => {
	const { format, style } = useCompanyLogoStyles(props.brandfetchData)
	const image =
		props.linearLogoUrl ?? format?.src ?? props.contactImageUrl ?? undefined
	return (
		<Avatar
			name={!format && "fallback" in props ? (props.fallback ?? "") : ""}
			image={image}
			size={props.size}
			className={cn(props.className, "[&_img]:object-contain")}
			style={style}
			onClick={props.onClick}
			roundness={props.roundness ?? (image ? "md" : "full")}
		/>
	)
})
